import { useCallback, useEffect } from "react";

type ScrollPosition = {
  element: HTMLElement;
  left: number;
  top: number;
};

const captureAncestorScrollPositions = (
  textarea: HTMLTextAreaElement,
): ScrollPosition[] => {
  const positions: ScrollPosition[] = [];

  for (
    let ancestor = textarea.parentElement;
    ancestor;
    ancestor = ancestor.parentElement
  ) {
    const isScrollable =
      ancestor.scrollHeight > ancestor.clientHeight ||
      ancestor.scrollWidth > ancestor.clientWidth;

    if (isScrollable || ancestor.scrollTop !== 0 || ancestor.scrollLeft !== 0) {
      positions.push({
        element: ancestor,
        left: ancestor.scrollLeft,
        top: ancestor.scrollTop,
      });
    }
  }

  return positions;
};

const restoreScrollPositions = (positions: ScrollPosition[]) => {
  for (const { element, left, top } of positions) {
    element.scrollLeft = left;
    element.scrollTop = top;
  }
};

/**
 * Auto-resizes a <textarea> element based on its content.
 * Ensures proper sizing by waiting for the DOM to be fully painted
 * and observing future changes using ResizeObserver.
 */
export const useTextAreaResize = (
  ref: React.RefObject<HTMLTextAreaElement>,
) => {
  // Function that resets the height to auto, then sets it based on scrollHeight
  const resize = useCallback(() => {
    const textarea = ref.current;
    if (textarea && textarea.offsetParent !== null) {
      // Collapsing a focused, very tall textarea can make the browser scroll an
      // ancestor to keep the caret visible. Restore every scroll container after
      // measuring so this internal layout step does not move the page.
      const scrollPositions = captureAncestorScrollPositions(textarea);
      textarea.style.height = "auto"; // Reset to allow shrink
      textarea.style.height = `${textarea.scrollHeight}px`; // Expand to fit content
      restoreScrollPositions(scrollPositions);
    }
  }, [ref]);

  useEffect(() => {
    if (!ref.current) return;

    let frameId: number | null = null;

    /**
     * Resizes the textarea on the next animation frame.
     * This ensures DOM has fully rendered before measuring height.
     */
    const resizeOnNextFrame = () => {
      if (frameId) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => resize());
    };

    // Initial resize when component mounts and the ref is ready
    resizeOnNextFrame();

    // Also resize on window resize (in case layout changes)
    window.addEventListener("resize", resizeOnNextFrame);

    /**
     * ResizeObserver triggers resize when the textarea's size changes
     * due to font loading, style changes, or layout shifts.
     */
    const observer = new ResizeObserver(() => resizeOnNextFrame());
    observer.observe(ref.current);

    // Cleanup listeners and animation frames on unmount
    return () => {
      window.removeEventListener("resize", resizeOnNextFrame);
      if (frameId) cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [ref, resize]);

  return resize;
};
